import type { SerializerOptions } from '@ember-data/legacy-compat';
import type { Snapshot } from '@ember-data/legacy-compat/-private';
import type { ModelSchema } from '@ember-data/store/types';

import type { RecordAttributes, RecordData } from 'webapp-types';
import { omit } from 'webapp/utils/common';

import ApplicationSerializer from 'webapp/serializers/application';

import type ScorecardRecord from 'webapp/models/scorecard';

export default class ScorecardSerializer extends ApplicationSerializer {
	override getReadOnlyFields(_json: Record<string, unknown>): RecordData.Scorecard.ReadOnlyKey[] {
		return ['cloneFrom'];
	}

	// json is Raw when it comes from BE, Serialized when BufferedModel.toJSON() runs a record through normalize
	override normalize(
		typeClass: ModelSchema,
		json: RecordData.Scorecard.Raw | RecordData.Scorecard.Serialized,
		prop: string | undefined,
	) {
		const { schedule } = json;
		// originateFrom from BE is ignored; scheduleDayOfWeek can be null before the array transform turns it into []
		const attributes: Partial<Omit<RecordAttributes<ScorecardRecord>, 'scheduleDayOfWeek'>> & {
			id?: string | number;
			scheduleDayOfWeek?: ScorecardRecord['scheduleDayOfWeek'] | null;
		} = omit(json, 'schedule', 'originateFrom');

		attributes.scheduleType = schedule.type;
		attributes.scheduleHourOfDay = schedule.hourOfDay === undefined ? null : schedule.hourOfDay;
		attributes.scheduleDayOfWeek = schedule.dayOfWeek === undefined ? null : schedule.dayOfWeek;
		attributes.scheduleDayOfMonth =
			schedule.dayOfMonth === undefined ? null : schedule.dayOfMonth;

		return super.normalize(typeClass, attributes, prop);
	}

	override serialize(
		snapshot: Snapshot,
		options: SerializerOptions,
	): RecordData.Scorecard.Serialized {
		// read-only fields are already removed by ApplicationSerializer
		const attributes = super.serialize(snapshot, options) as Omit<
			RecordAttributes<ScorecardRecord>,
			RecordData.Scorecard.ReadOnlyKey
		>;

		return {
			...omit(
				attributes,
				'scheduleType',
				'scheduleHourOfDay',
				'scheduleDayOfWeek',
				'scheduleDayOfMonth',
			),
			schedule: {
				type: attributes.scheduleType,
				hourOfDay: attributes.scheduleHourOfDay,
				dayOfWeek: attributes.scheduleDayOfWeek,
				dayOfMonth: attributes.scheduleDayOfMonth,
			},
		};
	}
}
